import React, { useEffect, useRef } from 'react';
import { VariantSelectorMessageProps } from '../types';
import { t, Lang } from '../../../../i18n';
import { textToSpeechService } from '../../../../services/textToSpeech';

// Shared formatting function for measurements (copied from BubblesContainer)
function formatMeasurement(key: string, value: any, lang: Lang = 'en'): string {
  // Handle array of stringified JSON objects
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    try {
      const parsed = JSON.parse(value[0]);
      return formatMeasurement(key, parsed, lang);
    } catch (e) {
      // fallback to showing the string if parsing fails
      return `${t(lang, key as any)}: ${value[0]}`;
    }
  }
  // Special formatting for 'size' (multiple shapes from backend)
  if (key === 'size' && typeof value === 'object' && value !== null) {
    if (Array.isArray(value.values) && value.unit) {
      return `${t(lang, key as any)}: ${value.values.join(' x ')} ${value.unit}`;
    }
    // size with min/max/unit (same shape as area)
    if ('min' in value || 'max' in value) {
      const min = value.min !== undefined && value.min !== null ? Number(value.min) : undefined;
      const max = value.max !== undefined && value.max !== null ? Number(value.max) : undefined;
      const unit = value.unit || '';
      if (min !== undefined && max !== undefined) {
        if (min === max) {
          return `${t(lang, key as any)}: ${min} ${unit}`;
        }
        return `${t(lang, key as any)}: ${min} ${unit} < ${t(lang, key as any)} < ${max} ${unit}`;
      }
      if (min !== undefined) {
        return `${t(lang, key as any)}: ${t(lang, key as any)} > ${min} ${unit}`;
      }
      if (max !== undefined) {
        return `${t(lang, key as any)}: ${t(lang, key as any)} < ${max} ${unit}`;
      }
    }
  }
  // Special formatting for 'area'
  if (
    key === 'area' &&
    typeof value === 'object' && value !== null &&
    ('min' in value || 'max' in value)
  ) {
    const min = value.min !== undefined && value.min !== null ? Number(value.min) : undefined;
    const max = value.max !== undefined && value.max !== null ? Number(value.max) : undefined;
    let unit = value.unit;
    if ((!unit || unit === null) && value.type === 'area') {
      unit = 'm2';
    } else if (!unit) {
      unit = '';
    }
    if (min !== undefined && max !== undefined) {
      if (min === max) {
        return `${t(lang, key as any)}: ${min} ${unit}`;
      } else {
        return `${t(lang, key as any)}: ${min} ${unit} < ${t(lang, key as any)} < ${max} ${unit}`;
      }
    } else if (min !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} > ${min} ${unit}`;
    } else if (max !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} < ${max} ${unit}`;
    }
  }
  // Special formatting for 'height', 'diameter', 'depth', and 'length'
  if (
    (key === 'height' || key === 'diameter' || key === 'depth' || key === 'length') &&
    typeof value === 'object' && value !== null &&
    ('min' in value || 'max' in value)
  ) {
    const min = value.min !== undefined && value.min !== null ? Number(value.min) : undefined;
    const max = value.max !== undefined && value.max !== null ? Number(value.max) : undefined;
    let unit = value.unit || '';
    if (min !== undefined && max !== undefined) {
      if (min === max) {
        return `${t(lang, key as any)}: ${min} ${unit}`;
      } else {
        return `${t(lang, key as any)}: ${min} ${unit} < ${t(lang, key as any)} < ${max} ${unit}`;
      }
    } else if (min !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} > ${min} ${unit}`;
    } else if (max !== undefined) {
      return `${t(lang, key as any)}: ${t(lang, key as any)} < ${max} ${unit}`;
    }
  }
  // Fallbacks
  if (Array.isArray(value)) {
    return `${t(lang, key as any)}: ${value.map(v => Array.isArray(v) ? v.join(', ') : v).join(', ')}`;
  } else if (typeof value === 'object' && value !== null) {
    // Only fallback to JSON if not a min/max/unit object
    if (!('min' in value) && !('max' in value)) {
      return `${t(lang, key as any)}: ${JSON.stringify(value)}`;
    } else {
      return '';
    }
  } else {
    return `${t(lang, key as any)}: ${String(value)}`;
  }
}

const VariantSelectorMessage: React.FC<VariantSelectorMessageProps & { selectedVariantId?: string, lang?: Lang }> = ({ message, onSelectVariant, selectedVariantId, lang = 'en' }) => {
  let variants = message.variants || {};
  
  // Fallback: If no variants exist, create one artificial variant with the task title
  if (Object.keys(variants).length === 0) {
    const taskTitle = message.taskData?.[0]?.description || message.text || 'Task';
    const mainTaskId = message.taskData?.[0]?.id || message.taskId || 'main_task';
    variants = {
      [mainTaskId]: {
        title: taskTitle,
        description: `Default option for: ${taskTitle}`
      }
    };
  }
  
  // Trigger audio when component mounts (only when user actually has to choose)
  useEffect(() => {
    if (Object.keys(variants).length > 1) {
      textToSpeechService.playAudio('multipleVariantsFound');
    }
  }, []);

  // Auto-select when there is only one variant – no need to show the selector
  const variantIds = Object.keys(variants);
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (variantIds.length === 1 && onSelectVariant && !didAutoSelect.current) {
      didAutoSelect.current = true;
      onSelectVariant(message, variantIds[0]);
    }
  }, [variantIds.length, variantIds[0], message, onSelectVariant]);
  
  // Extract task name from message text if it contains "for [task name]"
  // The message text format is: "Please select a variant for "[task description]":" or "Please select a variant for subtask [ID]:"
  // We only want to show the description, not the ID
  let taskNameInHeader = '';
  if (message.text) {
    // First try to match the quoted description (preferred)
    const quotedMatch = message.text.match(/for "([^"]+)"/);
    if (quotedMatch) {
      taskNameInHeader = quotedMatch[1];
    }
    // Don't fall back to ID - if we don't have a description, don't show anything in parentheses
  }
  
  // Build header text with task name if available (only if we have a description, not an ID)
  const headerText = taskNameInHeader
    ? `${t(lang, 'multipleVariantsFound')} (${taskNameInHeader})`
    : t(lang, 'multipleVariantsFound');
  
  return (
    <div className="variant-selector-message">
      <div className="bubble-label">{headerText}</div>
      <div className="variant-buttons">
        {Object.entries(variants).map(([variantId, variantObj]) => {
          const typedVariantObj = variantObj as Record<string, any>;
          
          // For artificial variant (when no real variants exist), use the title directly
          if (Object.keys(message.variants || {}).length === 0 && variantId === (message.taskData?.[0]?.id || message.taskId || 'main_task')) {
            return (
              <button
                key={variantId}
                className={`variant-btn${selectedVariantId === variantId ? ' selected' : ''}`}
                onClick={() => onSelectVariant(message, variantId)}
              >
                {typedVariantObj.title || t(lang, 'defaultOption')}
              </button>
            );
          }
          
          // Compose a label from the measurements for real variants
          // Exclude billing fields (billable_unit, quantity_result_obligation, price_per_unit_in_euro)
          const billingFieldsToExclude = ['billable_unit', 'quantity_result_obligation', 'price_per_unit_in_euro'];
          const label = Object.entries(typedVariantObj)
            .filter(([k]) => !billingFieldsToExclude.includes(k))
            .map(([k, v]) => formatMeasurement(k, v, lang))
            .filter(Boolean)
            .join(', ');
          return (
            <button
              key={variantId}
              className={`variant-btn${selectedVariantId === variantId ? ' selected' : ''}`}
              onClick={() => onSelectVariant(message, variantId)}
            >
              {label || variantId}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default VariantSelectorMessage; 